// /src/components/shared/ReportModal.jsx
// Shared modal for reporting content (articles, events, chats, comments)

import React, { useState } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { Flag } from 'lucide-react';
import { REPORT_TEXT_MAX } from '../../utils/validationConstants';
import Modal from '../core/Modal';

const ReportModal = ({
  visible,
  onClose,
  onSubmit,
  contentType, // 'article' | 'event' | 'chat' | 'comment'
  contentId
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState('');

  const categories = [
    { value: 'offensive', label: t('common:report_offensive') || 'Offensive or abusive' },
    { value: 'spam', label: t('common:report_spam') || 'Spam or self-promotion' },
    { value: 'harassment', label: t('common:report_harassment') || 'Harassment' },
    { value: 'off_topic', label: t('common:report_off_topic') || 'Off-topic for this group/locality' }
  ];

  const handleSubmit = async () => {
    if (!category) {
      setInlineError(t('common:select_report_category') || 'Please select a category');
      return;
    }
    setInlineError('');
    setSubmitting(true);
    try {
      await onSubmit({
        targetType: contentType,
        targetId: contentId,
        category,
        details: description.trim() || null
      });

      // Reset form
      setCategory('');
      setDescription('');
      onClose();
    } catch (error) {
      console.error('Error submitting report:', error);
      setInlineError(t('common:report_error') || 'Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={visible}
      onClose={onClose}
      panelStyle={{
        backgroundColor: theme.surface || 'white',
        borderRadius: '8px',
        padding: '24px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '80vh',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        margin: '20px'
      }}
    >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <Flag size={24} color={theme.error || '#DC2626'} />
          <h2 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: 600,
            color: theme.text
          }}>
            {t('common:report_content') || 'Report Content'}
          </h2>
        </div>

        {/* Category selection */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 500,
            color: theme.text
          }}>
            {t('common:report_category') || 'Category'}
            <span style={{ color: theme.error || '#DC2626' }}> *</span>
          </label>

          {categories.map((cat) => (
            <label
              key={cat.value}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px',
                marginBottom: '8px',
                backgroundColor: category === cat.value ? (theme.primary ? theme.primary + '10' : '#024B9910') : 'transparent',
                border: `1px solid ${category === cat.value ? (theme.primary || '#024B99') : (theme.border || '#e5e7eb')}`,
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <input
                type="radio"
                name="category"
                value={cat.value}
                checked={category === cat.value}
                onChange={(e) => setCategory(e.target.value)}
                style={{ marginRight: '10px', cursor: 'pointer' }}
              />
              <span style={{ color: theme.text }}>{cat.label}</span>
            </label>
          ))}
        </div>

        {/* Description (optional) */}
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontWeight: 500,
            color: theme.text
          }}>
            {t('common:report_description') || 'Additional details (optional)'}
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('common:report_description_placeholder') || 'Please provide any additional context...'}
            maxLength={REPORT_TEXT_MAX}
            rows={4}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '6px',
              border: `1px solid ${theme.border || '#d1d5db'}`,
              backgroundColor: theme.background || '#ffffff',
              color: theme.text || '#1f2937',
              fontFamily: 'OpenSauceSans, sans-serif',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
          <div style={{
            fontSize: '12px',
            color: theme.textSecondary,
            marginTop: '4px',
            textAlign: 'right'
          }}>
            {description.length}/500
          </div>
        </div>

        {/* Inline error */}
        {inlineError && (
          <p style={{ color: '#dc2626', fontSize: '13px', margin: '0 0 12px 0' }}>{inlineError}</p>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: `1px solid ${theme.border}`,
              backgroundColor: 'transparent',
              color: theme.text,
              fontFamily: 'OpenSauceSans, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              cursor: submitting ? 'not-allowed' : 'pointer',
              opacity: submitting ? 0.5 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            {t('common:cancel') || 'Cancel'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!category || submitting}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: !category || submitting ? theme.border : theme.error || '#DC2626',
              color: 'white',
              fontFamily: 'OpenSauceSans, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              cursor: !category || submitting ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {submitting ? t('common:submitting') || 'Submitting...' : t('common:submit_report') || 'Submit Report'}
          </button>
        </div>
    </Modal>
  );
};

export default ReportModal;
